import type { DetectionRegion, OcrLine } from '../types'
import { classifyOcrLines } from '../core/pii'

let workerPromise: Promise<import('tesseract.js').Worker> | null = null
let workerTail: Promise<void> = Promise.resolve()
let activeProgress: ((progress: number, label: string) => void) | undefined

function assetUrl(path: string): string {
  return new URL(path, document.baseURI).href
}

async function getWorker(onProgress?: (progress: number, label: string) => void): Promise<import('tesseract.js').Worker> {
  activeProgress = onProgress
  if (!workerPromise) {
    workerPromise = (async () => {
      const Tesseract = await import('tesseract.js')
      return Tesseract.createWorker(['jpn', 'eng'], Tesseract.OEM.LSTM_ONLY, {
        workerPath: assetUrl('./assets/tesseract/worker.min.js'),
        corePath: assetUrl('./assets/tesseract-core'),
        langPath: assetUrl('./assets/tessdata'),
        gzip: true,
        workerBlobURL: false,
        logger: (message) => {
          const progress = Number.isFinite(message.progress) ? message.progress : 0
          activeProgress?.(progress, translateStatus(message.status))
        },
        errorHandler: (error) => console.error('[OCR]', error),
      })
    })().catch((error) => {
      workerPromise = null
      throw error
    })
  }
  const worker = await workerPromise
  await worker.setParameters({
    tessedit_pageseg_mode: (await import('tesseract.js')).PSM.AUTO,
    preserve_interword_spaces: '1',
    user_defined_dpi: '180',
  })
  return worker
}

function translateStatus(status: string): string {
  const map: Record<string, string> = {
    'loading tesseract core': '文字認識エンジンを準備中',
    'initializing tesseract': '文字認識を初期化中',
    'loading language traineddata': '日本語辞書を読み込み中',
    'initializing api': '日本語OCRを準備中',
    'recognizing text': '画像内の文字を確認中',
  }
  return map[status] ?? '画像内の文字を確認中'
}

function extractLines(data: import('tesseract.js').Page, inverseScale: number): OcrLine[] {
  const lines: OcrLine[] = []
  for (const block of data.blocks ?? []) {
    for (const paragraph of block.paragraphs ?? []) {
      for (const line of paragraph.lines ?? []) {
        const text = line.text.trim()
        if (!text) continue
        lines.push({
          text,
          confidence: line.confidence,
          bbox: {
            x0: line.bbox.x0 * inverseScale,
            y0: line.bbox.y0 * inverseScale,
            x1: line.bbox.x1 * inverseScale,
            y1: line.bbox.y1 * inverseScale,
          },
          words: (line.words ?? []).filter((word) => word.text.trim()).map((word) => ({
            text: word.text,
            confidence: word.confidence,
            bbox: {
              x0: word.bbox.x0 * inverseScale,
              y0: word.bbox.y0 * inverseScale,
              x1: word.bbox.x1 * inverseScale,
              y1: word.bbox.y1 * inverseScale,
            },
            lineText: text,
          })),
        })
      }
    }
  }
  return lines
}

async function recognizeExclusive(
  canvas: HTMLCanvasElement,
  originalWidth: number,
  originalHeight: number,
  analysisScale: number,
  onProgress?: (progress: number, label: string) => void,
): Promise<DetectionRegion[]> {
  try {
    const worker = await getWorker(onProgress)
    const result = await worker.recognize(canvas, { rotateAuto: true }, { text: true, blocks: true })
    const lines = extractLines(result.data, 1 / analysisScale)
    return classifyOcrLines(lines, originalWidth, originalHeight)
  } finally {
    activeProgress = undefined
  }
}

export function detectJapanesePii(
  canvas: HTMLCanvasElement,
  originalWidth: number,
  originalHeight: number,
  analysisScale: number,
  onProgress?: (progress: number, label: string) => void,
): Promise<DetectionRegion[]> {
  const run = workerTail.then(() => recognizeExclusive(canvas, originalWidth, originalHeight, analysisScale, onProgress))
  workerTail = run.then(() => undefined, () => undefined)
  return run
}

export async function preloadOcr(): Promise<void> {
  await getWorker()
}

export async function terminateOcr(): Promise<void> {
  // terminate() destroys the worker transport. Wait until queued recognition has
  // settled so an in-flight job cannot attempt postMessage against a dead worker.
  await workerTail
  const currentWorker = workerPromise
  if (!currentWorker) return
  workerPromise = null
  const worker = await currentWorker
  await worker.terminate()
}
